import { useContext } from 'react'

import { useTranslation } from 'react-i18next'
import { connect, currentPageParams, useData } from 'scrivito'

import { EntityListContext } from './EntityListContext'
import { ErrorView } from './error/ErrorView'
import { DataClassName } from '@/Data/const'
import { ensureString } from '@/utils/ensureString'
import { paramsToFilters } from '@/utils/filterParams'

export const EntityListNoResultsIndicator = connect(
  function EntityListNoResultsIndicator() {
    const dataScope = useData()
    const { hasMore } = useContext(EntityListContext)
    const params = currentPageParams()
    const filter = ensureString(params['filter'] ?? null)
    const query = ensureString(params['query'] ?? null)
    const labels = ensureString(params['labels'] ?? null)
    const search = ensureString(params['search'] ?? null)
    const attributeFilters = paramsToFilters(params)
    const { t } = useTranslation()

    // subscribe to dataScope.take() and hasMore() loading
    const items = dataScope.take()
    const canLoadMore = hasMore()

    const hasActiveSearchOrFilter =
      !!search ||
      !!filter ||
      !!query ||
      !!labels ||
      Object.keys(attributeFilters).length > 0

    // The List "Object" filter is applied client-side (see
    // DXPOverviewWidgetComponent) — PISA's `/list` endpoint 422s on a
    // server-side `entityType` filter, so `dataScope.count()` still reflects
    // the unfiltered total and can't be used here.
    const entityTypeFilter =
      dataScope.dataClassName() === DataClassName.LIST
        ? attributeFilters.entityType
        : undefined
    const count = entityTypeFilter
      ? items.filter((item) => item.get('entityType') === entityTypeFilter)
          .length
      : dataScope.count()
    if (count === null || count > 0) {
      return null
    }
    // A zero count from the client-side entityType filter only covers the
    // page(s) fetched so far — later, unfetched pages (still server-filtered
    // only by the other criteria) may contain matches. Say so explicitly
    // rather than falsely claiming "no results", so the user knows to load
    // more instead of assuming the filter is broken.
    if (entityTypeFilter && canLoadMore) {
      return (
        <ErrorView
          headline={t('search.filterPending.title')}
          text={t('search.filterPending.subtitle')}
          image={<div className="spaceman-sad" />}
        />
      )
    }

    const translationKey = hasActiveSearchOrFilter ? 'noResults' : 'noData'

    return (
      <ErrorView
        headline={t(`search.${translationKey}.title`)}
        text={t(`search.${translationKey}.subtitle`)}
        image={<div className="spaceman-sad" />}
      />
    )
  },
  { loading: () => null },
)
